import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { taxContributionsService, userService } from '../services/api';
import { FileText, Plus, Edit, Trash2, Save, X, User, CheckCircle, XCircle, Eye, RefreshCw, TrendingUp, Building, Calculator, Layers } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmModal from './ConfirmModal';

const TaxContributionsManagement = () => {
  const queryClient = useQueryClient();
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingContribution, setEditingContribution] = useState(null);
  const [formData, setFormData] = useState({
    refYear: new Date().getFullYear(),
    refMonth: new Date().getMonth() + 1,
    inssValue: '',
    fgtsValue: '',
    inssPaid: false,
    fgtsPaid: false,
    inssPaidDate: '',
    fgtsPaidDate: '',
    notes: '',
  });
  const [files, setFiles] = useState({
    inss_guide: null,
    inss_receipt: null,
    fgts_guide: null,
    fgts_receipt: null,
  });
  const [confirmModal, setConfirmModal] = useState({ show: false, itemId: null, action: null });

  // Buscar funcionários
  const { data: usersData } = useQuery({
    queryKey: ['users-for-tax-contributions'],
    queryFn: () => userService.getUsers({ limit: 100 }),
    staleTime: 5 * 60 * 1000,
  });

  const users = useMemo(() => {
    if (!usersData) return [];
    if (Array.isArray(usersData?.data?.users)) return usersData.data.users;
    if (Array.isArray(usersData?.data?.data?.users)) return usersData.data.data.users;
    if (Array.isArray(usersData?.data)) return usersData.data;
    return [];
  }, [usersData]);

  // Buscar contribuições fiscais
  const { data: contributionsData, isLoading, refetch } = useQuery({
    queryKey: ['tax-contributions', selectedUserId, selectedYear, selectedMonth],
    queryFn: () =>
      taxContributionsService.getTaxContributions({
        userId: selectedUserId || undefined,
        year: selectedYear,
        month: selectedMonth,
      }),
    staleTime: 0,
  });

  const contributions = useMemo(() => {
    if (!contributionsData?.data?.data) return [];
    return Array.isArray(contributionsData.data.data) ? contributionsData.data.data : [];
  }, [contributionsData]);

  // Buscar apuração consolidada de encargos da empresa (Patronal + Segurados + FGTS)
  const {
    data: consolidationData,
    refetch: refetchConsolidation,
  } = useQuery({
    queryKey: ['tax-consolidation', selectedYear, selectedMonth],
    queryFn: () =>
      taxContributionsService.getTaxConsolidation({
        year: selectedYear,
        month: selectedMonth,
      }),
    staleTime: 0,
  });

  const totals = consolidationData?.data?.totals || null;
  const byType = consolidationData?.data?.byType || null;

  // Mutação para sincronização automática da folha em 1-clique
  const autoSyncMutation = useMutation({
    mutationFn: () =>
      taxContributionsService.autoSyncTaxContributions({
        year: selectedYear,
        month: selectedMonth,
      }),
    onSuccess: (res) => {
      toast.success(res?.data?.message || 'Contribuições sincronizadas com a folha!');
      refetch();
      refetchConsolidation();
      queryClient.invalidateQueries({ queryKey: ['tax-contributions'] });
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Erro ao sincronizar da folha.'),
  });

  // Mutação para criar/atualizar contribuição fiscal
  const upsertMutation = useMutation({
    mutationFn: (data) => {
      const submitData = {
        ...data,
        ...files,
      };
      
      if (editingContribution) {
        return taxContributionsService.updateTaxContribution(editingContribution.id, submitData);
      } else {
        return taxContributionsService.upsertTaxContribution(submitData);
      }
    },
    onSuccess: () => {
      toast.success(editingContribution ? 'Contribuição fiscal atualizada com sucesso!' : 'Contribuição fiscal criada com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['tax-contributions'] });
      setIsModalOpen(false);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao salvar contribuição fiscal');
    },
  });

  // Mutação para deletar contribuição fiscal
  const deleteMutation = useMutation({
    mutationFn: (id) => taxContributionsService.deleteTaxContribution(id),
    onSuccess: () => {
      toast.success('Contribuição fiscal deletada com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['tax-contributions'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao deletar contribuição fiscal');
    },
  });

  const resetForm = () => {
    setFormData({
      refYear: new Date().getFullYear(),
      refMonth: new Date().getMonth() + 1,
      inssValue: '',
      fgtsValue: '',
      inssPaid: false,
      fgtsPaid: false,
      inssPaidDate: '',
      fgtsPaidDate: '',
      notes: '',
    });
    setFiles({
      inss_guide: null,
      inss_receipt: null,
      fgts_guide: null,
      fgts_receipt: null,
    });
    setEditingContribution(null);
  };

  const handleOpenModal = (contribution = null) => {
    if (contribution) {
      setEditingContribution(contribution);
      setFormData({
        refYear: contribution.ref_year,
        refMonth: contribution.ref_month,
        inssValue: contribution.inss_value || '',
        fgtsValue: contribution.fgts_value || '',
        inssPaid: contribution.inss_paid || false,
        fgtsPaid: contribution.fgts_paid || false,
        inssPaidDate: contribution.inss_paid_date ? contribution.inss_paid_date.split('T')[0] : '',
        fgtsPaidDate: contribution.fgts_paid_date ? contribution.fgts_paid_date.split('T')[0] : '',
        notes: contribution.notes || '',
      });
      setFiles({
        inss_guide: null,
        inss_receipt: null,
        fgts_guide: null,
        fgts_receipt: null,
      });
    } else {
      resetForm();
    }
    setIsModalOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!selectedUserId) {
      toast.error('Selecione um funcionário');
      return;
    }

    const submitData = {
      userId: parseInt(selectedUserId),
      refYear: parseInt(formData.refYear),
      refMonth: parseInt(formData.refMonth),
      inssValue: formData.inssValue ? parseFloat(formData.inssValue) : 0,
      fgtsValue: formData.fgtsValue ? parseFloat(formData.fgtsValue) : 0,
      inssPaid: formData.inssPaid,
      fgtsPaid: formData.fgtsPaid,
      inssPaidDate: formData.inssPaidDate || null,
      fgtsPaidDate: formData.fgtsPaidDate || null,
      notes: formData.notes || null,
    };

    upsertMutation.mutate(submitData);
  };

  const handleFileChange = (field, file) => {
    setFiles(prev => ({
      ...prev,
      [field]: file,
    }));
  };

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const getFileUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_API_URL || ''}/${url}`;
  };

  const handleConfirm = () => {
    if (confirmModal.action === 'delete' && confirmModal.itemId) {
      deleteMutation.mutate(confirmModal.itemId);
    }
    setConfirmModal({ show: false, itemId: null, action: null });
  };

  const handleCancelConfirm = () => {
    setConfirmModal({ show: false, itemId: null, action: null });
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Contribuições Fiscais (INSS/FGTS)</h1>
        <p className="text-gray-600">Gerencie as informações contábeis e fiscais dos funcionários</p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Funcionário</label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Todos</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.full_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ano</label>
            <input
              type="number"
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              min="2000"
              max="2100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mês</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                <option key={month} value={month}>
                  {new Date(2000, month - 1).toLocaleDateString('pt-BR', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={() => handleOpenModal()}
              className="bg-primary-600 text-white px-3 py-2 rounded-md hover:bg-primary-700 flex items-center justify-center gap-1.5 text-sm font-medium transition-colors"
            >
              <Plus className="h-4 w-4" />
              Manual
            </button>
            <button
              onClick={() => autoSyncMutation.mutate()}
              disabled={autoSyncMutation.isPending}
              className="bg-emerald-600 text-white px-3 py-2 rounded-md hover:bg-emerald-700 flex items-center justify-center gap-1.5 text-sm font-medium disabled:opacity-50 transition-colors"
              title="Puxar valores calculados de INSS e FGTS dos holerites do mês"
            >
              <RefreshCw className={`h-4 w-4 ${autoSyncMutation.isPending ? 'animate-spin' : ''}`} />
              Sincronizar Folha
            </button>
          </div>
        </div>
      </div>

      {/* Cards Consolidados de Encargos da Empresa */}
      {totals && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {/* Base da Folha */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between text-gray-500 mb-1">
              <span className="text-xs font-semibold uppercase tracking-wider">Base da Folha</span>
              <Building className="h-4 w-4 text-gray-400" />
            </div>
            <div className="text-xl font-bold text-gray-900">
              {Number(totals.totalPayrollBase || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </div>
            <div className="text-[11px] text-gray-500 mt-1">
              Salário + Extras + Adicionais
            </div>
          </div>

          {/* INSS Segurados */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between text-blue-600 mb-1">
              <span className="text-xs font-semibold uppercase tracking-wider">INSS Retido</span>
              <User className="h-4 w-4 text-blue-500" />
            </div>
            <div className="text-xl font-bold text-blue-700">
              {Number(totals.totalInssEmployee || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </div>
            <div className="text-[11px] text-gray-500 mt-1">
              Descontado dos colaboradores
            </div>
          </div>

          {/* Encargos Patronais */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between text-purple-600 mb-1">
              <span className="text-xs font-semibold uppercase tracking-wider">INSS Patronal</span>
              <Calculator className="h-4 w-4 text-purple-500" />
            </div>
            <div className="text-xl font-bold text-purple-700">
              {(Number(totals.totalInssPatronal || 0) + Number(totals.totalRatFap || 0) + Number(totals.totalTerceiros || 0)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </div>
            <div className="text-[11px] text-gray-500 mt-1">
              Empresa (20%) + RAT (2%) + Terceiros (5.8%)
            </div>
          </div>

          {/* Total DARF Previdenciário */}
          <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl shadow-sm border border-indigo-200 p-4">
            <div className="flex items-center justify-between text-indigo-700 mb-1">
              <span className="text-xs font-bold uppercase tracking-wider">Guia DARF (GPS)</span>
              <FileText className="h-4 w-4 text-indigo-600" />
            </div>
            <div className="text-xl font-extrabold text-indigo-900">
              {Number(totals.totalDarfGeneral || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </div>
            <div className="text-[11px] text-indigo-700 mt-1 font-medium">
              Total Previdenciário a Recolher
            </div>
          </div>

          {/* FGTS Digital */}
          <div className="bg-emerald-50 rounded-xl shadow-sm border border-emerald-200 p-4">
            <div className="flex items-center justify-between text-emerald-700 mb-1">
              <span className="text-xs font-bold uppercase tracking-wider">FGTS Digital (8%)</span>
              <TrendingUp className="h-4 w-4 text-emerald-600" />
            </div>
            <div className="text-xl font-extrabold text-emerald-900">
              {Number(totals.totalFgtsCompany || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </div>
            <div className="text-[11px] text-emerald-700 mt-1 font-medium">
              Encargo exclusivo da empresa
            </div>
          </div>
        </div>
      )}

      {/* Discriminativo por Tipo de Folha */}
      {byType && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 mb-6 flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-2 font-semibold text-gray-700">
            <Layers className="h-4 w-4 text-gray-500" />
            Composição da Competência:
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <span className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-lg">
              Folha Regular: <strong>{byType.REGULAR?.count || 0} holerite(s)</strong> • Base: {Number(byType.REGULAR?.base || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </span>
            {(byType['13_FIRST_HALF']?.count > 0 || byType['13_SECOND_HALF']?.count > 0) && (
              <span className="px-2.5 py-1 bg-amber-50 text-amber-800 rounded-lg border border-amber-200">
                13º Salário: <strong>{(byType['13_FIRST_HALF']?.count || 0) + (byType['13_SECOND_HALF']?.count || 0)} parcela(s)</strong> • Base: {Number((byType['13_FIRST_HALF']?.base || 0) + (byType['13_SECOND_HALF']?.base || 0)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </span>
            )}
            {byType.TERMINATION?.count > 0 && (
              <span className="px-2.5 py-1 bg-red-50 text-red-800 rounded-lg border border-red-200">
                Rescisões: <strong>{byType.TERMINATION?.count} TRCT(s)</strong> • Base: {Number(byType.TERMINATION?.base || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Tabela */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Carregando...</div>
        ) : contributions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Nenhuma contribuição fiscal encontrada</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Funcionário</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Período</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">INSS</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">FGTS</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {contributions.map((contribution) => (
                  <tr key={contribution.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{contribution.user_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(contribution.ref_year, contribution.ref_month - 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatCurrency(contribution.inss_value)}</div>
                      <div className="text-xs text-gray-500">
                        {contribution.inss_paid ? (
                          <span className="text-green-600 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            Pago {contribution.inss_paid_date ? formatDate(contribution.inss_paid_date) : ''}
                          </span>
                        ) : (
                          <span className="text-red-600 flex items-center gap-1">
                            <XCircle className="h-3 w-3" />
                            Pendente
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatCurrency(contribution.fgts_value)}</div>
                      <div className="text-xs text-gray-500">
                        {contribution.fgts_paid ? (
                          <span className="text-green-600 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            Pago {contribution.fgts_paid_date ? formatDate(contribution.fgts_paid_date) : ''}
                          </span>
                        ) : (
                          <span className="text-red-600 flex items-center gap-1">
                            <XCircle className="h-3 w-3" />
                            Pendente
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex gap-2">
                        {contribution.inss_paid && contribution.fgts_paid ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Completo
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Pendente
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleOpenModal(contribution)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => {
                            setConfirmModal({ show: true, itemId: contribution.id, action: 'delete' });
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingContribution ? 'Editar Contribuição Fiscal' : 'Nova Contribuição Fiscal'}
                </h2>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Funcionário */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Funcionário *</label>
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Selecione um funcionário</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.full_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Período */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ano *</label>
                  <input
                    type="number"
                    value={formData.refYear}
                    onChange={(e) => setFormData({ ...formData, refYear: parseInt(e.target.value) })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    min="2000"
                    max="2100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mês *</label>
                  <select
                    value={formData.refMonth}
                    onChange={(e) => setFormData({ ...formData, refMonth: parseInt(e.target.value) })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                      <option key={month} value={month}>
                        {new Date(2000, month - 1).toLocaleDateString('pt-BR', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                </div>

                {/* INSS */}
                <div className="md:col-span-2 border-t pt-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">INSS</h3>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valor INSS (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.inssValue}
                    onChange={(e) => setFormData({ ...formData, inssValue: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data de Pagamento</label>
                  <input
                    type="date"
                    value={formData.inssPaidDate}
                    onChange={(e) => setFormData({ ...formData, inssPaidDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Guia do INSS</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange('inss_guide', e.target.files[0])}
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  {editingContribution?.inss_guide_url && !files.inss_guide && (
                    <a
                      href={getFileUrl(editingContribution.inss_guide_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary-600 hover:text-primary-800 mt-1 flex items-center gap-1"
                    >
                      <Eye className="h-4 w-4" />
                      Ver guia atual
                    </a>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Comprovante de Pagamento INSS</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange('inss_receipt', e.target.files[0])}
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  {editingContribution?.inss_receipt_url && !files.inss_receipt && (
                    <a
                      href={getFileUrl(editingContribution.inss_receipt_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary-600 hover:text-primary-800 mt-1 flex items-center gap-1"
                    >
                      <Eye className="h-4 w-4" />
                      Ver comprovante atual
                    </a>
                  )}
                </div>
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.inssPaid}
                      onChange={(e) => setFormData({ ...formData, inssPaid: e.target.checked })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700">INSS Pago</span>
                  </label>
                </div>

                {/* FGTS */}
                <div className="md:col-span-2 border-t pt-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">FGTS</h3>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valor FGTS (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.fgtsValue}
                    onChange={(e) => setFormData({ ...formData, fgtsValue: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data de Pagamento</label>
                  <input
                    type="date"
                    value={formData.fgtsPaidDate}
                    onChange={(e) => setFormData({ ...formData, fgtsPaidDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Guia do FGTS</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange('fgts_guide', e.target.files[0])}
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  {editingContribution?.fgts_guide_url && !files.fgts_guide && (
                    <a
                      href={getFileUrl(editingContribution.fgts_guide_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary-600 hover:text-primary-800 mt-1 flex items-center gap-1"
                    >
                      <Eye className="h-4 w-4" />
                      Ver guia atual
                    </a>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Comprovante de Pagamento FGTS</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileChange('fgts_receipt', e.target.files[0])}
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  {editingContribution?.fgts_receipt_url && !files.fgts_receipt && (
                    <a
                      href={getFileUrl(editingContribution.fgts_receipt_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary-600 hover:text-primary-800 mt-1 flex items-center gap-1"
                    >
                      <Eye className="h-4 w-4" />
                      Ver comprovante atual
                    </a>
                  )}
                </div>
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.fgtsPaid}
                      onChange={(e) => setFormData({ ...formData, fgtsPaid: e.target.checked })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700">FGTS Pago</span>
                  </label>
                </div>

                {/* Observações */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Observações adicionais..."
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={upsertMutation.isLoading}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Save className="h-5 w-5" />
                  {upsertMutation.isLoading ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmModal
        show={confirmModal.show}
        onConfirm={handleConfirm}
        onCancel={handleCancelConfirm}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja deletar esta contribuição fiscal?"
        confirmText="Deletar"
        cancelText="Cancelar"
        type="danger"
        isLoading={deleteMutation.isLoading}
      />
    </div>
  );
};

export default TaxContributionsManagement;
